// Обработка кеширования через DOM элементы
import { SizeEstimator } from './SizeEstimator.js';

export class DOMCacheHandler {
    constructor(imageCacheDB, cacheManager) {
        this.imageCacheDB = imageCacheDB;
        this.cacheManager = cacheManager;
        this.sizeEstimator = new SizeEstimator();
    }

    /**
     * Кеширование уже загруженного изображения из DOM
     * @param {HTMLElement} mediaElement - элемент img или video
     * @param {Object} postcard - данные открытки
     * @param {boolean} isPreview - является ли превью
     */
    async cacheLoadedImage(mediaElement, postcard, isPreview = false) {
        try {
            // Выбираем URL в зависимости от того, превью это или оригинал
            let imageUrl;
            if (isPreview) {
                imageUrl = postcard.preview_78x101_url || postcard.preview_36_url || postcard.imageUrl;
            } else {
                imageUrl = postcard.image_url || postcard.imageUrl;
            }
            
            const isVideo = imageUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);
            const isGif = imageUrl.match(/\.gif$/i);
            
            if (isVideo) {
                await this.cacheVideoFromDOM(mediaElement, postcard, imageUrl, isPreview);
            } else {
                await this.cacheImageFromDOM(mediaElement, postcard, imageUrl, isPreview, isGif);
            }
            
            return null; // Возвращаем null, так как не можем получить blob
        } catch (error) {
            console.log('DOM caching error:', error);
            return null;
        }
    }

    /**
     * Кеширование видео из DOM
     */
    async cacheVideoFromDOM(mediaElement, postcard, imageUrl, isPreview) {
        // Для видео проверяем готовность
        if (!mediaElement || mediaElement.readyState < 2) {
            return null;
        }
        
        // Пытаемся получить реальный размер видео через fetch
        let realSize = null;
        try {
            realSize = await this.sizeEstimator.getVideoSize(imageUrl);
        } catch (error) {
            // Используем оценку
        }
        
        // Используем реальный размер или оценку
        const finalSize = realSize || this.sizeEstimator.estimateVideoSize(
            mediaElement.videoWidth || mediaElement.width,
            mediaElement.videoHeight || mediaElement.height,
            mediaElement.duration || 10 // примерная длительность
        );
        
        const metadata = {
            url: imageUrl,
            postcardId: postcard.id,
            originalUrl: imageUrl,
            method: 'dom-loaded',
            loaded: true,
            timestamp: Date.now(),
            estimatedSize: finalSize,
            realSize: realSize, // Сохраняем реальный размер если получили
            type: 'video',
            isPreview: isPreview,
            dimensions: {
                width: mediaElement.videoWidth || mediaElement.width,
                height: mediaElement.videoHeight || mediaElement.height
            }
        };
        
        await this.imageCacheDB.saveImage(imageUrl, null, metadata);
    }

    /**
     * Кеширование изображения из DOM
     */
    async cacheImageFromDOM(mediaElement, postcard, imageUrl, isPreview, isGif) {
        // Для изображений и GIF
        if (!mediaElement || !mediaElement.complete || mediaElement.naturalWidth === 0) {
            return null;
        }
        
        // Вычисляем примерный размер изображения на основе его размеров
        const estimatedSize = this.sizeEstimator.estimateImageSize(
            mediaElement.naturalWidth, 
            mediaElement.naturalHeight
        );
        
        // Сохраняем метаданные в IndexedDB без blob, но с примерным размером
        const metadata = {
            url: imageUrl,
            postcardId: postcard.id,
            originalUrl: imageUrl,
            method: 'dom-loaded',
            loaded: true,
            timestamp: Date.now(),
            estimatedSize: estimatedSize,
            type: isGif ? 'gif' : 'image',
            isPreview: isPreview,
            dimensions: {
                width: mediaElement.naturalWidth,
                height: mediaElement.naturalHeight
            }
        };
        
        await this.imageCacheDB.saveImage(imageUrl, null, metadata);
    }

    /**
     * Регистрация изображения для кеширования после загрузки
     */
    registerImageForCaching(mediaElement, postcard, isPreview = false) {
        if (!mediaElement || !postcard) return;

        // Выбираем URL в зависимости от того, превью это или оригинал
        let imageUrl;
        if (isPreview) {
            imageUrl = postcard.preview_78x101_url || postcard.preview_36_url || postcard.imageUrl;
        } else {
            imageUrl = postcard.image_url || postcard.imageUrl;
        }
        
        if (!imageUrl) return;

        const isVideo = imageUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);
        const isGif = imageUrl.match(/\.gif$/i);

        // Проверяем, не кешировано ли уже
        this.imageCacheDB.getImage(imageUrl).then(cachedData => {
            if (cachedData) {
                return;
            }

            if (isVideo) {
                // Для видео
                if (mediaElement.readyState >= 2) {
                    this.cacheLoadedImage(mediaElement, postcard, isPreview);
                } else {
                    mediaElement.addEventListener('loadeddata', () => {
                        this.cacheLoadedImage(mediaElement, postcard, isPreview);
                    }, { once: true });
                }
            } else {
                // Для изображений и GIF
                if (mediaElement.complete && mediaElement.naturalWidth > 0) {
                    this.cacheLoadedImage(mediaElement, postcard, isPreview);
                } else {
                    mediaElement.addEventListener('load', () => {
                        this.cacheLoadedImage(mediaElement, postcard, isPreview);
                    }, { once: true });
                }
            }
        });
    }

    /**
     * Регистрация всех изображений на странице
     */
    registerAllImagesOnPage(postcards, isPreview = false) {
        if (!postcards || !Array.isArray(postcards)) return;

        postcards.forEach(postcard => {
            // Выбираем URL в зависимости от того, превью это или оригинал
            let imageUrl;
            if (isPreview) {
                imageUrl = postcard.preview_78x101_url || postcard.preview_36_url || postcard.imageUrl;
            } else {
                imageUrl = postcard.image_url || postcard.imageUrl;
            }
            
            if (!imageUrl) return;

            const isVideo = imageUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);
            const isGif = imageUrl.match(/\.gif$/i);

            // Проверяем, есть ли элемент с таким URL на странице
            let foundElement = null;
            
            if (isVideo) {
                // Ищем video элементы
                const videoElements = document.querySelectorAll('video');
                for (const video of videoElements) {
                    if (video.src === imageUrl || video.currentSrc === imageUrl) {
                        foundElement = video;
                        break;
                    }
                }
            } else {
                // Ищем img элементы
                const imgElements = document.querySelectorAll('img');
                for (const img of imgElements) {
                    if (img.src === imageUrl || img.currentSrc === imageUrl) {
                        foundElement = img;
                        break;
                    }
                }
            }
            
            if (foundElement) {
                this.registerImageForCaching(foundElement, postcard, isPreview);
            }
        });
    }

    /**
     * Регистрация изображения по селектору
     */
    registerImageBySelector(selector, postcard) {
        const mediaElement = document.querySelector(selector);
        if (mediaElement) {
            this.registerImageForCaching(mediaElement, postcard);
        }
    }
}
